import { ChangeDetectionStrategy, Component, computed, effect, inject, resource, signal } from '@angular/core';
import { DxDataGridModule, DxTagBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DxiButtonModule, DxiColumnModule, DxiValidationRuleModule, DxoColumnFixingModule, DxoEditingModule, DxoHeaderFilterModule, DxoPopupModule, DxoSearchPanelModule } from 'devextreme-angular/ui/nested';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { GridService } from '../shared/grid/grid.service';
import { GenericModalService } from '../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { CurrentUserService } from './services/current-user.service';
import { DataGroupService } from './services/data-group.service';
import { UserGroupService } from './services/user-group.service';
import { UserRoleService } from './services/user-role.service';
import { DataGroupPresentation, UserGroup, UserRole } from './models';
import { DxTagBoxTypes } from 'devextreme-angular/ui/tag-box';
import { ToastService, ToastType } from '../shared/toast/toast.service';

@Component({
    templateUrl: './user-group-crud.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule,
        DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxoHeaderFilterModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective,
        DxiButtonModule, DxTemplateModule, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxoSearchPanelModule, DxTagBoxModule
    ]
})
export class UserGroupCrudComponent {
    private userGroupService = inject(UserGroupService);
    private loadingPanelService = inject(LoadingPanelService);
    private readonly modalService = inject(GenericModalService);
    private readonly currentUserService = inject(CurrentUserService);
    private readonly gridService = inject(GridService);
    private readonly userRoleService = inject(UserRoleService);
    private readonly dataGroupService = inject(DataGroupService);
    private readonly toastService = inject(ToastService);
    readonly bizGridFiltervalues = inject(BizGridFiltervalues);

    userGroupsResource = resource({
        params: () => ({}),
        loader: () => this.userGroupService.getUserGroups(),
    });

    availableRolesResource = resource({
        params: () => ({}),
        loader: () => this.userRoleService.getUserRoles(),
    });

    availableDataGroupsResource = resource({
        params: () => ({}),
        loader: () => this.dataGroupService.getDataGroups(false, false),
    });

    private loadingEffect = effect(() => {
        if (this.userGroupsResource.isLoading()) {
            this.loadingPanelService.showLoadingPanelDirect('Loading user groups...');
        } else {
            this.loadingPanelService.hideLoadingPanel();
        }
    });

    readonly createAllowed = signal(this.currentUserService.hasPermission('UserGroup_Action_Create'));
    readonly editAllowed = signal(this.currentUserService.hasPermission('UserGroup_Action_Edit'));
    readonly deleteAllowed = signal(this.currentUserService.hasPermission('UserGroup_Action_Delete'));
    readonly showLogAllowed = signal(this.currentUserService.hasPermission('UserGroup_Action_ShowLog'));

    readonly availableRoles = computed(() => this.availableRolesResource.value()?.sort((r1, r2) => r1.RoleName.localeCompare(r2.RoleName)) ?? []);
    readonly availableDataGroups = computed(() => this.availableDataGroupsResource.value()?.sort((dg1, dg2) => dg1.Name.localeCompare(dg2.Name)) ?? []);
    userGroups = computed(() => this.userGroupsResource.value()?.filter(group => !group.IsStakeholder)?.sort((g1, g2) => g1.Name.localeCompare(g2.Name)) ?? []);

    onGroupInserting(e: DxDataGridTypes.RowInsertingEvent<UserGroup>): void {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.saveWithValidation(item, true));
    }

    onEditingStart(e: DxDataGridTypes.EditingStartEvent<UserGroup>): void {
        if (e.data) {
            const group = e.data;
            const containsSuperAdminPermission = group.Roles.some(r => r.Permissions.some(p => p.PermissionType === 'SuperAdmin'));
            if (containsSuperAdminPermission) {
                void this.modalService.messageDirect('Not allowed', 'Only SuperAdmins can edit usergroups containing the SuperAdmin permission.').catch(() => undefined);
                e.cancel = true;
            }
        }
    }

    onGroupUpdating(e: DxDataGridTypes.RowUpdatingEvent<UserGroup>): void {
        // Check the group-name on an update if it's different from its previous value
        const checkGroupName = e.newData.Name ? e.newData.Name.toLowerCase() !== e.oldData.Name.toLowerCase() : false;
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.saveWithValidation(item, checkGroupName), e.newData);
    }

    onGroupRemoving(e: DxDataGridTypes.RowRemovingEvent<UserGroup>): void {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.removeUserGroup(item));
    }

    onRolesValueChanged(cell: { setValue: (value: UserRole[]) => void }, event: DxTagBoxTypes.ValueChangedEvent): void {
        if (event.event) {
            const selectedIds = event.value as number[];
            cell.setValue(this.availableRoles().filter(r => selectedIds.includes(r.RoleId)));
        }
    }

    onDataGroupsValueChanged(cell: { setValue: (value: DataGroupPresentation[]) => void }, event: DxTagBoxTypes.ValueChangedEvent): void {
        if (event.event) {
            const selectedIds = event.value as number[];
            cell.setValue(this.availableDataGroups().filter(dg => selectedIds.includes(dg.Id)));
        }
    }

    getRoleIds(roles: UserRole[] | null | undefined): number[] {
        return (roles ?? []).map(r => r.RoleId);
    }

    getDataGroupIds(dataGroups: DataGroupPresentation[] | null | undefined): number[] {
        return (dataGroups ?? []).map(dg => dg.Id);
    }

    getRolesHeaderFilterData = (): Array<{ text: string; value: string; }> =>
        this.userGroups()
            .flatMap(e => e.Roles?.length > 0 ? e.Roles.map(r => r.RoleName) : ['(Blanks)'])
            .filter((name, idx, self) => self.indexOf(name) === idx)
            .sort((a, b) => a.localeCompare(b))
            .map(roleName => ({ text: roleName, value: roleName }));

    getDataGroupsHeaderFilterData = (): Array<{ text: string; value: string; }> =>
        this.userGroups()
            .flatMap(e => e.DataGroups?.length > 0 ? e.DataGroups.map(dg => dg.Name) : ['(Blanks)'])
            .filter((name, idx, self) => self.indexOf(name) === idx)
            .sort((a, b) => a.localeCompare(b))
            .map(dataGroupName => ({ text: dataGroupName, value: dataGroupName }));

    calculateArrayRolesFilterExpression(filterValue: string): (rowData: UserGroup) => boolean {
        // Custom filter for array fields
        return (rowData: UserGroup): boolean => {
            let hasMatch = false;
            if (filterValue === '(Blanks)') {
                // Check if there are no permissions
                hasMatch = !rowData.Roles || rowData.Roles.length === 0;
            }

            if (filterValue !== '(Blanks)') {
                hasMatch = rowData.Roles?.some(role =>
                    role.RoleName?.toLowerCase().includes(filterValue.toLowerCase())
                ) ?? false;
            }

            return hasMatch;
        };
    }

    calculateArrayDataGroupsFilterExpression(filterValue: string): (rowData: UserGroup) => boolean {
        // Custom filter for array fields
        return (rowData: UserGroup): boolean => {
            let hasMatch = false;
            if (filterValue === '(Blanks)') {
                // Check if there are no permissions
                hasMatch = !rowData.DataGroups || rowData.DataGroups.length === 0;
            }

            if (filterValue !== '(Blanks)') {
                hasMatch = rowData.DataGroups?.some(dataGroup =>
                    dataGroup.Name?.toLowerCase().includes(filterValue.toLowerCase())
                ) ?? false;
            }

            return hasMatch;
        };
    }

    // Sorting and filtering DataGroups
    calculateSortValueDataGroups(rowData: UserGroup): string {
        // Sort by name - concatenate to a string
        const datagroups: DataGroupPresentation[] = [...(rowData.DataGroups ?? [])];
        return datagroups.sort((a, b) => a.Name.localeCompare(b.Name)).map((r) => r.Name).join('');
    }

    // Sorting and filtering roles
    calculateSortValue(rowData: UserGroup): string {
        // Sort by name - concatenate to a string
        const roles: UserRole[] = [...(rowData.Roles ?? [])];
        return roles.sort((a, b) => a.RoleName.localeCompare(b.RoleName)).map((r) => r.RoleName).join('');
    }

    private async saveWithValidation(groupToSave: UserGroup, checkGroupName: boolean): Promise<UserGroup> {
        // Block save when a changed/new name already exists.
        if (checkGroupName) {
            const groupNameAvailable = await this.userGroupService.groupNameAvailable(groupToSave.Name);
            if (!groupNameAvailable) {
                await this.modalService.messageDirect('Name not available', 'The name is already in use by another Usergroup.').catch(() => undefined);
                return null;
            }
        }

        return this.saveUserGroup(groupToSave);
    }

    private async saveUserGroup(item: UserGroup): Promise<UserGroup> {
        this.loadingPanelService.showLoadingPanelDirect('Saving...');
        try {
            const savedItem = await this.userGroupService.saveUserGroup(item);
            this.toastService.showToast('User group saved...', ToastType.success);
            return savedItem;
        }
        catch {
            this.toastService.showToast('Failed to save user group...', ToastType.error);
            return null;
          }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    private async removeUserGroup(item: UserGroup): Promise<UserGroup> {
        this.loadingPanelService.showLoadingPanelDirect('Deleting...');
        try {
            await this.userGroupService.deleteUserGroup(item.Id);
            this.toastService.showToast('User group deleted....', ToastType.success);
            return item;
        }
        catch {
            this.toastService.showToast('Failed to delete user group...', ToastType.error);
            return null;
          }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }
}
